import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Loader2, AlertTriangle, Check, Users, Briefcase, CheckCircle2,
} from 'lucide-react';
import {
  assignPersonAgreement,
  assignPersonAgreementBounded,
  listAgreements,
} from '../services/api';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';
import { agreementsForCompany, isGlobalAgreement } from '../utils/agreements';

// Modal de ASIGNACIÓN MASIVA de convenios. Visible solo para super admin
// (el control de permisos lo hace el padre, que es EquipoPage).
//
// Recibe la lista de trabajadores seleccionados y un convenio + fechas.
// Lanza una llamada por cada trabajador (en serie con un pequeño throttle
// implícito por el await) y muestra el progreso. Si alguna falla, se sigue
// con las siguientes y al final se muestra el detalle.
//
// Modos:
//   · "Desde una fecha en adelante"   → assignPersonAgreement (solo validFrom).
//   · "Periodo cerrado"                → assignPersonAgreementBounded.

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const BulkAssignAgreementModal = ({ open, workers, onClose, onCompleted }) => {
  const [agreements, setAgreements] = useState([]);
  const [formAgreementId, setFormAgreementId] = useState('');
  const [formMode, setFormMode] = useState('open');
  const [formValidFrom, setFormValidFrom] = useState(todayIso());
  const [formValidTo, setFormValidTo] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  // Progreso por trabajador: id → { status, message? }
  // status: 'pending' | 'ok' | 'error'
  const [progress, setProgress] = useState(new Map());
  const [finished, setFinished] = useState(false);
  const toast = useToast();

  // Empresa de los trabajadores seleccionados. Si todos son de la misma
  // (el caso normal) ofrecemos sus convenios + los globales — importa para el
  // superadmin, que recibe los de todas las empresas. Si la selección mezcla
  // empresas, solo los GLOBALES: uno de empresa daría 404 ("Agreement not
  // found") en los trabajadores de las demás.
  const selectionCompanyIds = useMemo(
    () => new Set((workers || []).map((w) => Number(w?.companyId)).filter(Number.isInteger)),
    [workers]
  );
  const selectionCompanyId = selectionCompanyIds.size === 1 ? [...selectionCompanyIds][0] : null;
  const mixedCompanies = selectionCompanyIds.size > 1;

  const agreementOptions = useMemo(
    () => (mixedCompanies
      ? agreements.filter((a) => a.companyId == null)
      : agreementsForCompany(agreements, selectionCompanyId)),
    [agreements, mixedCompanies, selectionCompanyId]
  );

  useEffect(() => {
    if (!open) return;
    listAgreements(selectionCompanyId)
      .then((arr) => setAgreements(Array.isArray(arr) ? arr : []))
      .catch(() => setAgreements([]));
    setFormAgreementId('');
    setFormMode('open');
    setFormValidFrom(todayIso());
    setFormValidTo('');
    setRunning(false);
    setError(null);
    setProgress(new Map());
    setFinished(false);
  }, [open, selectionCompanyId]);

  if (!open) return null;

  const list = Array.isArray(workers) ? workers : [];
  const okCount = Array.from(progress.values()).filter((p) => p.status === 'ok').length;
  const errCount = Array.from(progress.values()).filter((p) => p.status === 'error').length;

  const handleRun = async () => {
    setError(null);
    if (!formAgreementId) { setError('Selecciona un convenio.'); return; }
    if (!formValidFrom)   { setError('Indica la fecha de inicio.'); return; }
    if (formMode === 'bounded') {
      if (!formValidTo) { setError('En periodo cerrado debes indicar la fecha fin.'); return; }
      if (formValidTo < formValidFrom) { setError('La fecha fin no puede ser anterior al inicio.'); return; }
    }
    if (list.length === 0) { setError('No hay trabajadores seleccionados.'); return; }

    setRunning(true);
    setFinished(false);
    // Marcar todos como pendientes.
    const initial = new Map();
    for (const w of list) initial.set(w.id, { status: 'pending' });
    setProgress(initial);

    // Lanzamos en SERIE para no saturar el backend ni levantar 429.
    // Si más adelante se necesitara más velocidad, se puede pasar a Promise.all
    // por batches de 5. Por ahora 1 a 1 es seguro y trazable.
    let okTotal = 0;
    let failTotal = 0;
    for (const w of list) {
      try {
        const payload = {
          personId: w.id,
          agreementId: Number(formAgreementId),
          validFrom: formValidFrom,
        };
        if (formMode === 'bounded') {
          await assignPersonAgreementBounded({ ...payload, validTo: formValidTo });
        } else {
          await assignPersonAgreement(payload);
        }
        okTotal += 1;
        setProgress((prev) => {
          const m = new Map(prev);
          m.set(w.id, { status: 'ok' });
          return m;
        });
      } catch (err) {
        failTotal += 1;
        setProgress((prev) => {
          const m = new Map(prev);
          m.set(w.id, { status: 'error', message: err?.message || 'Error desconocido' });
          return m;
        });
      }
    }
    setRunning(false);
    setFinished(true);
    // Un único resumen (el detalle por trabajador sigue en el modal).
    const summary = FEEDBACK.agreementBulkAssigned({ ok: okTotal, failed: failTotal, agreementName });
    toast.show(summary.type, summary.message);
    if (onCompleted) onCompleted();
  };

  const agreementName = (() => {
    const ag = agreements.find((a) => Number(a.id) === Number(formAgreementId));
    return ag ? ag.name : null;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-1.5">
              <Users size={12} /> Asignación masiva
            </div>
            <h2 className="font-display text-lg font-bold text-ink truncate">
              Asignar convenio a {list.length} trabajador{list.length === 1 ? '' : 'es'}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={running}
            className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Form de selección — bloqueado mientras corre o termina. */}
          {!finished && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">Convenio</label>
                <select
                  value={formAgreementId}
                  onChange={(e) => setFormAgreementId(e.target.value)}
                  disabled={running}
                  className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
                >
                  <option value="">— Selecciona —</option>
                  {agreementOptions.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.vacationDays ?? '—'}d vacaciones){isGlobalAgreement(a) ? ' · global' : ''}
                    </option>
                  ))}
                </select>
                {mixedCompanies && (
                  <div className="text-[10px] text-ink-muted mt-1 leading-tight">
                    Los trabajadores seleccionados son de varias empresas: solo se ofrecen convenios globales.
                  </div>
                )}
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">Tipo de asignación</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormMode('open')}
                    disabled={running}
                    className={`text-left text-xs rounded-md border-2 px-2.5 py-2 transition-colors ${
                      formMode === 'open'
                        ? 'border-primary bg-white text-ink'
                        : 'border-slate-200 bg-white/50 text-ink-muted hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold">Desde una fecha en adelante</div>
                    <div className="text-[10px] mt-0.5 leading-tight">Sin fecha fin. Cierra automáticamente el convenio vigente de cada trabajador.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormMode('bounded')}
                    disabled={running}
                    className={`text-left text-xs rounded-md border-2 px-2.5 py-2 transition-colors ${
                      formMode === 'bounded'
                        ? 'border-primary bg-white text-ink'
                        : 'border-slate-200 bg-white/50 text-ink-muted hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold">Periodo cerrado</div>
                    <div className="text-[10px] mt-0.5 leading-tight">Con fecha de fin. Útil para tramos pasados.</div>
                  </button>
                </div>
              </div>

              <div className={`grid gap-2 ${formMode === 'bounded' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">Desde</label>
                  <input
                    type="date"
                    value={formValidFrom}
                    onChange={(e) => setFormValidFrom(e.target.value)}
                    disabled={running}
                    className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 tabular-nums disabled:opacity-50"
                  />
                </div>
                {formMode === 'bounded' && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">Hasta</label>
                    <input
                      type="date"
                      value={formValidTo}
                      onChange={(e) => setFormValidTo(e.target.value)}
                      min={formValidFrom || undefined}
                      disabled={running}
                      className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 tabular-nums disabled:opacity-50"
                    />
                  </div>
                )}
              </div>

              {/* Resumen */}
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-xs text-ink">
                <div className="font-semibold mb-1 inline-flex items-center gap-1.5">
                  <Briefcase size={11} /> Resumen
                </div>
                <div>
                  Se asignará <b>{agreementName || '— convenio sin elegir —'}</b> a{' '}
                  <b>{list.length} trabajador{list.length === 1 ? '' : 'es'}</b>,{' '}
                  {formMode === 'bounded'
                    ? <>desde el <b>{formValidFrom || '—'}</b> hasta el <b>{formValidTo || '—'}</b>.</>
                    : <>desde el <b>{formValidFrom || '—'}</b> en adelante.</>}
                </div>
              </div>
            </>
          )}

          {error && !running && (
            <div className="rounded-lg border border-danger/30 bg-danger/5 p-3 flex items-start gap-2 text-xs">
              <AlertTriangle size={13} className="text-danger shrink-0 mt-0.5" />
              <div className="text-ink font-semibold">{error}</div>
            </div>
          )}

          {/* Listado de trabajadores con su estado en vivo. */}
          {(running || finished) && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold mb-1.5 flex items-center justify-between">
                <span>Progreso</span>
                <span className="tabular-nums">
                  {okCount + errCount} / {list.length} · <span className="text-success">{okCount} OK</span>
                  {errCount > 0 && <> · <span className="text-danger">{errCount} con error</span></>}
                </span>
              </div>
              <ul className="border border-slate-200 rounded-md max-h-64 overflow-y-auto divide-y divide-slate-100">
                {list.map((w) => {
                  const p = progress.get(w.id) || { status: 'pending' };
                  return (
                    <li key={w.id} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                      <div className="shrink-0">
                        {p.status === 'pending' && <Loader2 size={12} className="animate-spin text-ink-muted" />}
                        {p.status === 'ok' && <CheckCircle2 size={12} className="text-success" />}
                        {p.status === 'error' && <AlertTriangle size={12} className="text-danger" />}
                      </div>
                      <div className="flex-1 min-w-0 truncate text-ink">{w.name || `Trabajador #${w.id}`}</div>
                      {p.status === 'error' && (
                        <div className="text-[10px] text-danger truncate max-w-[200px]" title={p.message}>
                          {p.message}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 shrink-0">
          {!finished ? (
            <div className="flex items-center gap-2 justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={running}
                className="px-3 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleRun}
                disabled={running || list.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-60"
              >
                {running ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                {running
                  ? `Asignando… (${okCount + errCount}/${list.length})`
                  : `Asignar a ${list.length}`}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-ink-muted">
                {errCount === 0
                  ? <><CheckCircle2 size={12} className="inline text-success" /> Todo completado correctamente.</>
                  : <><AlertTriangle size={12} className="inline text-warning" /> Hubo {errCount} error{errCount === 1 ? '' : 'es'}. Revisa el listado.</>}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary-dark transition-colors"
              >
                Cerrar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkAssignAgreementModal;
